import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Race } from '../../types';
import { raceService } from '../../services';
import { getRegionLabel, getRegionRoman } from '../../utils/regionMapper';
import { RaceCard } from './RaceCard';
import './RaceListSidebar.css';

interface RaceListSidebarProps {
  filteredRaces: Race[];
  selectedRace: Race | null;
  hoveredRaceId: string | null;
  onRaceSelect: (race: Race | null) => void;
  onRaceHover: (race: Race | null) => void;
  regionTotalCounts?: Record<string, number>;
}

export function RaceListSidebar({
  filteredRaces,
  selectedRace,
  onRaceSelect,
  onRaceHover,
  regionTotalCounts,
}: RaceListSidebarProps) {
  const { t } = useTranslation('races');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const groupedSorted = useMemo(() => {
    const groups = raceService.groupByRegion(filteredRaces);
    const regions = Object.keys(groups).sort();
    return regions.map((region) => ({
      region,
      races: raceService.sortByActionability(groups[region]),
    }));
  }, [filteredRaces]);

  const toggleGroup = (region: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(region)) {
        next.delete(region);
      } else {
        next.add(region);
      }
      return next;
    });
  };

  return (
    <div className="race-sidebar">
      <div className="race-sidebar-header">
        <h3>{t('sidebar.title')}</h3>
        <div className="race-sidebar-count">
          <span className="race-sidebar-count-num display">{filteredRaces.length}</span>
          <span className="race-sidebar-count-label">{t('sidebar.countUnit')}</span>
        </div>
      </div>

      <div className="race-sidebar-list">
        {filteredRaces.length === 0 ? (
          <div className="race-sidebar-empty">
            <div className="race-sidebar-empty-headline display">{t('sidebar.empty.headline')}</div>
            <div className="race-sidebar-empty-hint">
              {t('sidebar.empty.hint').split('\n').map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </div>
          </div>
        ) : (
          groupedSorted.map(({ region, races }) => {
            const isCollapsed = collapsedGroups.has(region);
            const total = regionTotalCounts?.[region];
            const roman = getRegionRoman(region);

            return (
              <div
                key={region}
                className={`race-sidebar-group ${isCollapsed ? 'is-collapsed' : ''}`}
              >
                <div
                  className="race-sidebar-group-header"
                  role="button"
                  tabIndex={0}
                  aria-expanded={!isCollapsed}
                  aria-controls={`region-group-${region}`}
                  onClick={() => toggleGroup(region)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGroup(region);
                    }
                  }}
                >
                  <div className="race-sidebar-group-heading">
                    <span className="race-sidebar-group-label display">
                      {getRegionLabel(region)}
                    </span>
                    {roman && (
                      <span className="race-sidebar-group-roman">{roman}</span>
                    )}
                  </div>
                  <div className="race-sidebar-group-tally">
                    <span className="race-sidebar-group-count">
                      <span className="race-sidebar-group-count-current display">
                        {races.length}
                      </span>
                      {total !== undefined && total !== races.length && (
                        <>
                          <span className="race-sidebar-group-count-sep" aria-hidden="true">
                            /
                          </span>
                          <span className="race-sidebar-group-count-total">{total}</span>
                        </>
                      )}
                    </span>
                    <span
                      className={`race-sidebar-group-arrow ${isCollapsed ? 'is-collapsed' : ''}`}
                      aria-hidden="true"
                    />
                  </div>
                </div>
                {!isCollapsed && (
                  <div
                    id={`region-group-${region}`}
                    className="race-sidebar-group-cards"
                  >
                    {races.map((race) => (
                      <RaceCard
                        key={race.id}
                        race={race}
                        isActive={selectedRace?.id === race.id}
                        onSelect={onRaceSelect}
                        onHover={onRaceHover}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
